import { PRE_LOGIN_IMAGES, PreLoginHighlightIcon, PreLoginWorkflowIcon } from './preLoginDashboardImages';

export type { PreLoginHighlightIcon, PreLoginWorkflowIcon };

export interface PreLoginHighlight {
  title: string;
  description: string;
  icon: PreLoginHighlightIcon;
}

export interface PreLoginWorkflowStep {
  step: string;
  title: string;
  description: string;
  icon: PreLoginWorkflowIcon;
}

export const PRE_LOGIN_HERO = {
  title: 'Sustainability reporting and carbon management',
  subtitle:
    'Connect operational data with AI-enabled carbon intelligence for decision-grade emissions insights—from utility bills and activity records to buyer-ready disclosure.',
  chipLabel: 'CARBON WORKSPACE',
  registerAriaLabel: 'Register your MSME',
  loginAriaLabel: 'Sign in to your MSME carbon workspace',
  loginLabel: 'Sign in',
  contactLabel: 'Contact us',
  contactEmail: 'contact@sustainow.in',
  image: PRE_LOGIN_IMAGES.hero,
  imageAlt:
    'Sustainow dashboard preview showing scope-wise emissions, trends, and operational carbon signals.',
};

export const PRE_LOGIN_VALUE_PROPOSITION = {
  title: 'Embed carbon and ESG insights into your business processes',
  description:
    'Build stakeholder trust and transparency by turning sustainability data into actionable insights. Sustainow helps Indian MSMEs measure scope 1–3 emissions, identify reduction opportunities, and stay disclosure-ready without a full in-house ESG team.',
};

export interface PreLoginPillar {
  title: string;
  description: string;
}

export const PRE_LOGIN_PILLARS: PreLoginPillar[] = [
  {
    title: 'Integrated data intelligence',
    description:
      'Harmonise bills, exports, and operational signals into one workspace with verified emission factors and scope-wise breakdowns.',
  },
  {
    title: 'Comprehensive decision-making',
    description:
      'Embed carbon visibility into daily operations with dashboards, compliance outputs, and hotspot analysis for transparent planning.',
  },
  {
    title: 'Accelerated transition',
    description:
      'Track savings initiatives, align with buyer and lender expectations, and progress toward targets with less manual effort.',
  },
];

export interface PreLoginFaqItem {
  question: string;
  answer: string;
}

export interface PreLoginComplianceFramework {
  name: string;
  subtitle: string;
  description: string;
  track: 'india' | 'export';
}

export const COMPLIANCE_REPORTING_SECTION = {
  title: 'Buyer-ready compliance reporting',
  description:
    'Move from spreadsheets to structured disclosure packs. Sustainow maps your operational data to India-market and export frameworks — with readiness checks, gap lists, and PDF exports when your inventory is complete.',
  image: PRE_LOGIN_IMAGES.benefitsReporting,
  imageAlt: 'Compliance hub with BRSR, ISO, and CBAM reporting readiness.',
  indiaHeading: 'India market & domestic buyers',
  exportHeading: 'Export customers & global frameworks',
  ctaRegister: 'Register to start reporting',
  ctaLogin: 'Sign in',
};

export const COMPLIANCE_REPORTING_FRAMEWORKS: PreLoginComplianceFramework[] = [
  {
    name: 'BRSR Core',
    subtitle: 'SEBI value-chain disclosure',
    description: 'Supplier packs and Principle 6 emissions datapoints for large corporate buyers.',
    track: 'india',
  },
  {
    name: 'ISO 14064',
    subtitle: 'Organizational GHG inventory',
    description: 'Scope 1–3 inventories with assurance-prep workflows and gap closure lists.',
    track: 'india',
  },
  {
    name: 'ISO 14067',
    subtitle: 'Product carbon footprint',
    description: 'PCF packs for products you manufacture, with cradle-to-gate activity mapping.',
    track: 'india',
  },
  {
    name: 'PAT / energy intensity',
    subtitle: 'India efficiency programmes',
    description: 'Energy-linked emissions and intensity signals for PAT-relevant operations.',
    track: 'india',
  },
  {
    name: 'CBAM',
    subtitle: 'EU carbon border adjustment',
    description: 'Embedded emissions monitoring and registry-ready exports for EU-bound goods.',
    track: 'export',
  },
  {
    name: 'CSRD',
    subtitle: 'EU sustainability reporting',
    description: 'Structured datapoints for customers subject to EU corporate sustainability rules.',
    track: 'export',
  },
  {
    name: 'CDP & TCFD / ISSB',
    subtitle: 'Investor & climate disclosure',
    description: 'Climate risk and emissions questionnaires aligned to common buyer templates.',
    track: 'export',
  },
  {
    name: 'GRI · EUDR · EcoVadis',
    subtitle: 'Supply-chain questionnaires',
    description: 'Buyer audit packs, deforestation evidence, and scorecard-oriented exports.',
    track: 'export',
  },
];

export const PRE_LOGIN_FAQ: PreLoginFaqItem[] = [
  {
    question: 'What emission scopes does Sustainow cover?',
    answer:
      'Sustainow supports scope 1, 2, and 3 measurement using activity data from utilities, materials, logistics, and other operational inputs mapped to verified emission factors.',
  },
  {
    question: 'Is Sustainow built for Indian MSMEs?',
    answer:
      'Yes. The platform is designed for single-site manufacturers, processors, and service units—with templates for BRSR value-chain disclosures, energy intensity tracking, and Indian carbon-market readiness.',
  },
  {
    question: 'What reporting frameworks can I align with?',
    answer:
      'Sustainow supports BRSR Core supplier packs, ISO 14064 organizational inventories, ISO 14067 product carbon footprints, CBAM embedded-emissions reporting, CSRD datapoints, CDP, TCFD/ISSB, GRI, EUDR, and EcoVadis-oriented buyer exports — with readiness checks before you generate PDFs.',
  },
  {
    question: 'How do carbon offsets work on the platform?',
    answer:
      'Every workspace includes access to verified offset projects, a credits wallet, and retirement workflows aligned with emerging Indian carbon-market programmes.',
  },
];

export const PRE_LOGIN_FOOTER_CTA = {
  title: 'Ready to measure at your scale?',
  loginLabel: 'Sign in',
  registerLabel: 'Register your MSME',
};

export {
  PATENT_PENDING_HIGHLIGHT,
} from './aboutUsContent';

export const WHY_MEASUREMENT_MATTERS: PreLoginHighlight[] = [
  {
    title: 'Climate risk',
    description: 'See emissions alongside cost and operations.',
    icon: 'climate',
  },
  {
    title: 'Buyer & lender proof',
    description: 'Structured datapoints for procurement and finance.',
    icon: 'finance',
  },
  {
    title: 'Targeted reduction',
    description: 'Find hotspots in energy, materials, and logistics.',
    icon: 'reduction',
  },
  {
    title: 'India compliance',
    description: 'BRSR, energy intensity, and carbon-market readiness.',
    icon: 'compliance',
  },
];

export const CLIMATE_AND_SAVINGS_HIGHLIGHTS: PreLoginHighlight[] = [
  {
    title: 'Scope 1–3 visibility',
    description: 'Totals, trends, and category breakdowns.',
    icon: 'scope',
  },
  {
    title: 'Savings tracking',
    description: 'Quantify avoided emissions from improvements.',
    icon: 'savings',
  },
  {
    title: 'Cost–carbon link',
    description: 'Spot waste in spend and resource lines.',
    icon: 'insights',
  },
  {
    title: 'Carbon offsets',
    description: 'Purchase verified projects and retire green credits.',
    icon: 'offsets',
  },
];

export const PLATFORM_BENEFITS: PreLoginHighlight[] = [
  {
    title: 'Document capture',
    description: 'Bills and exports mapped to emission factors.',
    icon: 'documents',
  },
  {
    title: 'Control center',
    description: 'Live footprint, documents, and scope breakdown.',
    icon: 'dashboard',
  },
  {
    title: 'Compliance reporting',
    description: 'BRSR, ISO 14064/14067, CBAM, CSRD, and buyer-ready PDF exports.',
    icon: 'compliance',
  },
  {
    title: 'Works at your scale',
    description: 'Lightweight templates for MSMEs; depth for enterprise teams and integrations.',
    icon: 'scale',
  },
];

export const INDIA_SPECIFIC_INFO: PreLoginHighlight[] = [
  {
    title: 'BRSR value chain',
    description: 'Supplier packs for large corporate buyers.',
    icon: 'compliance',
  },
  {
    title: 'Energy intensity',
    description: 'Track PAT-relevant energy-linked emissions.',
    icon: 'energy',
  },
  {
    title: 'Carbon market',
    description: 'Credits and registry alignment as programmes mature.',
    icon: 'market',
  },
  {
    title: 'MSMEs & supply chains',
    description: 'Udyam to enterprise programs across domestic manufacturing networks.',
    icon: 'msme',
  },
];

export const CARBON_OFFSET_TEASER_SECTION = {
  title: 'Carbon offsets & green credits',
  description:
    'Purchase verified offset projects, manage your credits wallet, and retire green credits aligned with the Indian Carbon Market — included for every workspace at no extra subscription tier.',
  image: PRE_LOGIN_IMAGES.climateSavings,
  imageAlt: 'Carbon offset marketplace with projects, wallet, and trade history.',
  ctaMarketplace: 'Explore carbon offsets',
  ctaRegister: 'Register free',
  ctaLogin: 'Sign in',
};

export const PRICING_TEASER_SECTION = {
  title: 'Plans for every business stage',
  description:
    'Transparent MSME pricing: Free · Starter ₹1,999/mo · Core ₹3,499/mo · Growth ₹5,999/mo · Scale from ₹10,833/mo equiv. Prices exclude GST.',
  image: PRE_LOGIN_IMAGES.pricing,
  imageAlt: 'Pricing and green-finance plans overview.',
  ctaPricing: 'View pricing & plans',
  ctaRegister: 'Register free',
  ctaLogin: 'Sign in',
  planPriceChips: [
    { label: 'Free', amount: '₹0' },
    { label: 'Starter', amount: '₹1,999/mo' },
    { label: 'Core', amount: '₹3,499/mo' },
    { label: 'Growth', amount: '₹5,999/mo' },
    { label: 'Scale', amount: 'from ₹10,833/mo' },
  ],
};

export const POST_LOGIN_WORKFLOW: PreLoginWorkflowStep[] = [
  {
    step: '1',
    title: 'Register & profile',
    description: 'Sector, operations, and emission drivers.',
    icon: 'register',
  },
  {
    step: '2',
    title: 'Connect data',
    description: 'Upload bills or sync accounting exports.',
    icon: 'connect',
  },
  {
    step: '3',
    title: 'Carbon assessment',
    description: 'Baseline scopes and reduction opportunities.',
    icon: 'assess',
  },
  {
    step: '4',
    title: 'Report & improve',
    description: 'Dashboard, compliance hub, and cycles.',
    icon: 'report',
  },
];
